import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nnm_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nnm_token');
      localStorage.removeItem('nnm_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Techniciens
export const techniciensAPI = {
  getAll: () => api.get('/techniciens'),
  getById: (id) => api.get(`/techniciens/${id}`),
  create: (data) => api.post('/techniciens', data),
  update: (id, data) => api.patch(`/techniciens/${id}`, data),
  delete: (id, pin) => api.delete(`/techniciens/${id}`, { params: { pin } }),
  getSoldes: (id) => api.get(`/techniciens/${id}/soldes`),
  updateSoldes: (id, data, pin) => api.post(`/techniciens/${id}/soldes`, data, { params: { pin } }),
};

// Admin
export const adminAPI = {
  verifyPin: (pin) => api.post('/admin/verify-pin', { pin }),
};

// Clients
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Motos
export const motosAPI = {
  getAll: () => api.get('/motos'),
  getById: (id) => api.get(`/motos/${id}`),
  getByClient: (clientId) => api.get(`/motos/client/${clientId}`),
  create: (data) => api.post('/motos', data),
  update: (id, data) => api.patch(`/motos/${id}`, data),
  search: (q) => api.get('/motos/search', { params: { q } }),
  getHistorique: (id) => api.get(`/motos/${id}/historique`),
  getAlertesRevision: () => api.get('/motos/alertes-revision'),
  importCSV: (data) => api.post('/motos/import-csv', data),
};

// Exports CSV
export const exportAPI = {
  ordresTravail: (params) => api.get('/export/ordres-travail', { params, responseType: 'blob' }),
  motosAlertes: () => api.get('/export/motos-alertes', { responseType: 'blob' }),
  techniciensVT: (dateFrom, dateTo) => api.get('/export/techniciens-vt', { 
    params: { date_from: dateFrom, date_to: dateTo }, 
    responseType: 'blob' 
  }),
  // PDF exports
  otPDF: (otId) => api.get(`/export/pdf/ot/${otId}`, { responseType: 'blob' }),
  testRidePDF: (rideId) => api.get(`/export/pdf/test-ride/${rideId}`, { responseType: 'blob' }),
};

// Tentatives d'appel
export const tentativesAppelAPI = {
  getAll: (motoId, clientId) => api.get('/tentatives-appel', { params: { moto_id: motoId, client_id: clientId } }),
  create: (data) => api.post('/tentatives-appel', data),
  getRappels: () => api.get('/tentatives-appel/rappels'),
  getCalendrier: (year, month) => api.get(`/tentatives-appel/calendrier/${year}/${month}`),
};

// Ordres de Travail
export const ordresTravailAPI = {
  getAll: (statut) => api.get('/ordres-travail', { params: { statut } }),
  getById: (id) => api.get(`/ordres-travail/${id}`),
  create: (data) => api.post('/ordres-travail', data),
  update: (id, data) => api.patch(`/ordres-travail/${id}`, data),
  saisieVT: (id, lignes) => api.post(`/ordres-travail/${id}/saisie-vt`, { lignes }),
  terminer: (id, kmSortie) => api.post(`/ordres-travail/${id}/terminer`, { km_sortie: kmSortie }),
};

// Planning
export const planningAPI = {
  getSlots: (dateDebut, dateFin) => api.get('/planning/slots', { params: { date_debut: dateDebut, date_fin: dateFin } }),
  createSlot: (data) => api.post('/planning/slots', data),
  updateSlot: (id, data) => api.patch(`/planning/slots/${id}`, data),
  deleteSlot: (id) => api.delete(`/planning/slots/${id}`),
  getCharge: (date) => api.get(`/planning/charge/${date}`),
  getJour: (date) => api.get(`/planning/jour/${date}`),
};

// Leads (Pipeline)
export const leadsAPI = {
  getAll: (statut) => api.get('/leads', { params: { statut } }),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.patch(`/leads/${id}`, data),
};

// Prestations
export const prestationsAPI = {
  getAll: () => api.get('/prestations'),
};

// Absences
export const absencesAPI = {
  getAll: (technicienId, annee) => api.get('/absences', { params: { technicien_id: technicienId, annee } }),
  create: (data) => api.post('/absences', data),
  delete: (id) => api.delete(`/absences/${id}`),
  getCalendrier: (year, month) => api.get(`/absences/calendrier/${year}/${month}`),
};

// Automatismes
export const automatismesAPI = {
  getAll: (statut) => api.get('/automatismes', { params: { statut } }),
  getStats: () => api.get('/automatismes/stats'),
  update: (id, data) => api.patch(`/automatismes/${id}`, data),
  envoyer: (id) => api.post(`/automatismes/${id}/envoyer`),
  ignorer: (id) => api.post(`/automatismes/${id}/ignorer`),
  relancer: (id) => api.post(`/automatismes/${id}/relancer`),
  getRegles: () => api.get('/regles-automatismes'),
  updateRegle: (id, data) => api.patch(`/regles-automatismes/${id}`, data),
};

// Rapports Hebdomadaires
export const rapportsAPI = {
  getPreview: (semainesAvant = 0) => api.get('/rapports/hebdo/preview', { params: { semaines_avant: semainesAvant } }),
  getKPIs: (dateDebut, dateFin) => api.get('/rapports/hebdo/kpis', { params: { date_debut: dateDebut, date_fin: dateFin } }),
  getTendances: (nbSemaines = 6) => api.get('/rapports/hebdo/tendances', { params: { nb_semaines: nbSemaines } }),
  envoyer: (semainesAvant = 1, destinataires = []) => api.post('/rapports/hebdo/envoyer', { semaines_avant: semainesAvant, destinataires }),
  getHistorique: () => api.get('/rapports/historique'),
  getConfig: () => api.get('/rapports/config'),
  updateConfig: (data) => api.patch('/rapports/config', data),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Bexio
export const bexioAPI = {
  getStatus: () => api.get('/bexio/status'),
  getAuthUrl: () => api.get('/bexio/auth-url'),
  sync: () => api.post('/bexio/sync'),
  syncContacts: () => api.post('/bexio/sync/contacts'),
  syncInvoices: () => api.post('/bexio/sync/invoices'),
  syncOffers: () => api.post('/bexio/sync/offers'),
  getContacts: () => api.get('/bexio/contacts'),
  getInvoices: () => api.get('/bexio/invoices'),
  getOffers: () => api.get('/bexio/offers'),
  getLocalInvoices: (limit = 100) => api.get('/bexio/local/invoices', { params: { limit } }),
  getLocalOffers: (limit = 100) => api.get('/bexio/local/offers', { params: { limit } }),
  linkInvoiceToOT: (otId, bexioInvoiceId) => api.post('/bexio/link-invoice-to-ot', { ot_id: otId, bexio_invoice_id: bexioInvoiceId }),
  linkOfferToOT: (otId, bexioOfferId) => api.post('/bexio/link-offer-to-ot', { ot_id: otId, bexio_offer_id: bexioOfferId }),
  getSummary: () => api.get('/bexio/summary'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// VT Entries (Saisie temps réalisés)
export const vtEntriesAPI = {
  getAll: (params) => api.get('/vt-entries', { params }),
  create: (data) => api.post('/vt-entries', data),
  update: (id, data) => api.patch(`/vt-entries/${id}`, data),
  delete: (id) => api.delete(`/vt-entries/${id}`),
  getStats: (dateFrom, dateTo) => api.get('/vt-entries/stats', { params: { date_from: dateFrom, date_to: dateTo } }),
  getByOT: (otId) => api.get(`/vt-entries/by-ot/${otId}`),
};

// Test Fleet (Flotte d'essai)
export const testFleetAPI = {
  getAll: (actifOnly = true) => api.get('/test-fleet', { params: { actif_only: actifOnly } }),
  getById: (id) => api.get(`/test-fleet/${id}`),
  create: (data) => api.post('/test-fleet', data),
  update: (id, data) => api.patch(`/test-fleet/${id}`, data),
  delete: (id) => api.delete(`/test-fleet/${id}`),
  resolveBikeCode: (code) => api.get(`/test-fleet/resolve/${encodeURIComponent(code)}`),
};

// Test Rides (Essais)
export const testRidesAPI = {
  getAll: (statut, dateFilter) => api.get('/test-rides', { params: { statut, date_filter: dateFilter } }),
  getStats: () => api.get('/test-rides/stats'),
  getById: (id) => api.get(`/test-rides/${id}`),
  create: (data) => api.post('/test-rides', data),
  update: (id, data) => api.patch(`/test-rides/${id}`, data),
  start: (id, bikeScan) => api.post(`/test-rides/${id}/start`, null, { params: { bike_scan: bikeScan } }),
  returnBike: (id, bikeScan) => api.post(`/test-rides/${id}/return`, null, { params: { bike_scan: bikeScan } }),
  cancel: (id) => api.post(`/test-rides/${id}/cancel`),
  convertToLead: (id) => api.post(`/test-rides/${id}/convert-to-lead`),
  exportCSV: (rideIds) => api.get('/test-rides/export/csv', { params: { ride_ids: rideIds }, responseType: 'blob' }),
  uploadPermit: (formData) => api.post('/test-rides/upload/permit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadSignature: (signatureData) => api.post('/test-rides/upload/signature', { signature: signatureData }),
};

// License Management
export const licenseAPI = {
  getModules: () => api.get('/license/modules'),
  getCurrent: () => api.get('/license/current'),
  activate: (licenseKey) => api.post('/license/activate', { license_key: licenseKey }),
  getAll: () => api.get('/license/all'),
  create: (data) => api.post('/license/create', data),
  update: (id, data) => api.patch(`/license/${id}`, data),
  delete: (id) => api.delete(`/license/${id}`),
};

// Notifications
export const notificationsAPI = {
  getAll: (limit = 50, unreadOnly = false) => api.get('/notifications', { params: { limit, unread_only: unreadOnly } }),
  markRead: (notificationIds) => api.post('/notifications/mark-read', { notification_ids: notificationIds }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  sendTest: () => api.post('/notifications/test'),
  getStreamUrl: () => `${api.defaults.baseURL}/notifications/stream`,
};

// Audit Logs
export const auditAPI = {
  getLogs: (params = {}) => api.get('/audit-logs', { params }),
  getEntityLogs: (entityType, entityId) => api.get(`/audit-logs/entity/${entityType}/${entityId}`),
  getStats: () => api.get('/audit-logs/stats'),
};

export default api;
